/**
 * Mobile-internal player assignment hook for Free Agents page.
 * Per Tony's mobile architecture rule: mobile-internal copy of desktop usePlayerAssignment.
 *
 * 4-branch contract resolution (Bible § 4.5):
 *   a. minors → zero contract (salary=0, contract_years=0)
 *   b. offseason + projected_contract → keeper contract
 *   c. in-season owned → league_context.financial
 *   d. FA pickup → baseline_price + league.salary_settings.standard_contract_length
 *
 * NO FALLBACKS: throws on missing league_player, assignment.type, assignment.slotId,
 * baseline_price, contract_years.
 *
 * Side effects:
 *   - dispatches 'roster-updated' window event on successful add (Bible § B.5.6)
 *   - listens for 'roster-updated' event → re-fires loadRosterCapacity
 *   - navigate(/leagues/{id}) post-successful-add
 *
 * 9o.6.d: setError destructure warning resolved (was banked in 9o.5 port). Both desktop and
 * mobile copies dropped setError from the callbacks destructure — never used in this function;
 * error paths route via onError(msg) which callers wire to their own setError.
 *
 * Shared dependencies (allowed): leaguesAPI, react-router-dom useNavigate, RosterCapacityUtils.
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaguesAPI } from '../../../../../services/apiService';
// DD-MODAL-0723: tell the modal's ownership-bearing session cache the world changed.
import { invalidateLeaguePlayers } from '../../../components/player/hooks/playerDataCache';
import {
  analyzeRosterCapacity,
  canAddPlayers,
  findBestPositionForPlayer,
} from '../../../../../utils/RosterCapacityUtils';

export const useMobilePlayerAssignment = (leagueId, league, options = {}) => {
  const navigate = useNavigate();
  const {
    transactionsEnabled = false,
    leagueStatus = 'setup',
    isCommissionerMode = false,
    activeTeamName = '',
    getTargetTeamId = () => null,
    // user = null, // pre-existing unused — banked, kept verbatim
  } = options;

  // Modal State
  const [showPositionModal, setShowPositionModal] = useState(false);
  const [showBulkAssignmentModal, setShowBulkAssignmentModal] = useState(false);
  const [playerForAssignment, setPlayerForAssignment] = useState(null);

  // Roster State
  const [currentRoster, setCurrentRoster] = useState([]);
  const [rosterCapacityAnalysis, setRosterCapacityAnalysis] = useState(null);

  // UI State
  const [addingPlayer, setAddingPlayer] = useState(null);

  const loadRosterCapacity = useCallback(async () => {
    try {
      let rosterResponse;
      if (isCommissionerMode) {
        const targetTeamId = getTargetTeamId(null);
        if (targetTeamId) {
          rosterResponse = await leaguesAPI.getTeamRosterCanonical(leagueId, targetTeamId);
        } else {
          rosterResponse = await leaguesAPI.getMyRosterCanonical(leagueId);
        }
      } else {
        rosterResponse = await leaguesAPI.getMyRosterCanonical(leagueId);
      }

      const players = rosterResponse?.players || [];
      if (players.length > 0 || rosterResponse?.success) {
        // Filter out former/inactive players — accrued stats but not on roster
        const activePlayers = players.filter((p) => {
          const status = p.league_context?.roster?.status;
          return status && status !== 'former' && status !== 'inactive';
        });
        setCurrentRoster(activePlayers);
        const analysis = analyzeRosterCapacity(league, activePlayers);
        setRosterCapacityAnalysis(analysis);
        return analysis;
      }
      console.error('Failed to load roster for capacity analysis');
    } catch (err) {
      console.error('Error loading roster capacity:', err);
    }
    return null;
  }, [leagueId, league, isCommissionerMode, getTargetTeamId]);

  const handleAddPlayer = useCallback(
    async (player, setError) => {
      const isOfflineDraft = leagueStatus === 'offline_draft';
      const isDraftMode = ['draft', 'offline_draft'].includes(leagueStatus);

      if (!isOfflineDraft && !isDraftMode && !isCommissionerMode && !transactionsEnabled) {
        setError('Transactions are not allowed until the commissioner sets player prices');
        setTimeout(() => setError(''), 5000);
        return;
      }

      const isOwned = player.league_context?.team?.team_name;
      if (isOwned) {
        setError('Player is already owned by a team');
        setTimeout(() => setError(''), 5000);
        return;
      }

      // Always refetch — capacity may have changed since last load
      const freshAnalysis = await loadRosterCapacity();
      const analysis = freshAnalysis || rosterCapacityAnalysis;
      if (!analysis) {
        setError('Could not load roster capacity. Please try again.');
        setTimeout(() => setError(''), 5000);
        return;
      }

      const capacityCheck = canAddPlayers(1, analysis);
      if (!capacityCheck.canAdd) {
        setError(`Cannot add player: ${capacityCheck.message}`);
        setTimeout(() => setError(''), 5000);
        return;
      }

      const suggestion = findBestPositionForPlayer(player, analysis, league);
      if (!suggestion.hasSuggestions) {
        const firstName = player.info?.first_name;
        const lastName = player.info?.last_name;
        setError(`No available roster slots for ${firstName} ${lastName}`);
        setTimeout(() => setError(''), 5000);
        return;
      }

      setPlayerForAssignment(player);
      setShowPositionModal(true);
    },
    [transactionsEnabled, leagueStatus, isCommissionerMode, rosterCapacityAnalysis, league, loadRosterCapacity],
  );

  const executePlayerAssignment = useCallback(
    async (player, assignment, callbacks = {}) => {
      // 9o.6.d: setError removed from destructure — never used in this function (all error paths
      // call onError, which callers wire to their own setError). Eliminates banked warning;
      // matches desktop usePlayerAssignment.js post-9o.6.d.
      const { onSuccess, onError, onPlayerAdded } = callbacks;
      const firstName = player.info?.first_name;
      const lastName = player.info?.last_name;
      const leaguePlayerId = player.ids?.league_player;

      setAddingPlayer(leaguePlayerId);

      try {
        // Bible Rule 9: missing required data is a bug — throw, not fallback.
        if (!leaguePlayerId) {
          throw new Error(
            `Cannot add player: missing ids.league_player (mlb=${player.ids?.mlb}, ` +
              `name=${player.info?.full_name}). Player object likely came from non-canonical source.`,
          );
        }
        if (!assignment?.type) {
          throw new Error('Cannot add player: assignment.type is required');
        }
        if (assignment.type === 'active' && !assignment.slotId) {
          throw new Error(
            `Cannot add ${player.info?.full_name} to active roster: missing assignment.slotId`,
          );
        }

        // 4-branch contract resolution per Bible § 4.5
        const isOffseason = ['setup', 'draft', 'finalizing'].includes(leagueStatus);
        const isOwnedAlready = player.league_context?.status === 'owned';
        const isMinors = assignment.type === 'minors';
        const playerName = player.info?.full_name;

        let playerSalary;
        let contractYears;
        if (isMinors) {
          playerSalary = 0;
          contractYears = 0;
        } else if (isOffseason && player.projected_contract) {
          playerSalary = player.projected_contract.baseline_price;
          contractYears = player.projected_contract.contract_years;
          if (playerSalary == null) {
            throw new Error(`Cannot add ${playerName}: missing projected_contract.baseline_price`);
          }
          if (contractYears == null) {
            throw new Error(`Cannot add ${playerName}: missing projected_contract.contract_years`);
          }
        } else if (isOwnedAlready) {
          playerSalary = player.league_context.financial?.contract_salary;
          contractYears = player.league_context.financial?.contract_years;
          if (playerSalary == null) {
            throw new Error(
              `Cannot add ${playerName}: missing league_context.financial.contract_salary on owned player`,
            );
          }
          if (contractYears == null) {
            throw new Error(
              `Cannot add ${playerName}: missing league_context.financial.contract_years on owned player`,
            );
          }
        } else {
          // FA pickup: Bible § 4.5.1 + § 10
          playerSalary = player.league_context?.financial?.baseline_price;
          contractYears = league?.salary_settings?.standard_contract_length;
          if (playerSalary == null) {
            throw new Error(
              `Cannot add ${playerName}: missing baseline_price on league_context.financial. ` +
                `Pricing engine may not have run for this league.`,
            );
          }
          if (contractYears == null) {
            throw new Error(
              `Cannot add ${playerName}: league.salary_settings.standard_contract_length is not configured.`,
            );
          }
        }

        const playerData = {
          league_player_id: leaguePlayerId,
          salary: playerSalary,
          contract_years: contractYears,
          roster_status: assignment.type,
          roster_position: assignment.type === 'active' ? assignment.slotId : null,
          start_contract: !isMinors,
        };

        if (isCommissionerMode) {
          const targetId = getTargetTeamId(null);
          playerData.commissioner_action = true;
          playerData.target_team_id = targetId;
        }

        const response = await leaguesAPI.addPlayerToTeam(leagueId, playerData);
        invalidateLeaguePlayers(leagueId);

        if (response.success) {
          const teamName = isCommissionerMode ? activeTeamName : 'your team';
          const positionText =
            assignment.type === 'active' ? assignment.position.toUpperCase() : assignment.type;

          if (onSuccess) {
            onSuccess(`${response.player_name || firstName + ' ' + lastName} added to ${teamName} (${positionText})!`);
          }

          await loadRosterCapacity();
          window.dispatchEvent(new Event('roster-updated'));

          if (onPlayerAdded) {
            onPlayerAdded(player);
          }

          setTimeout(() => {
            navigate(`/leagues/${leagueId}`, {
              state: { activeSection: 'my-roster' },
              replace: false,
            });
          }, 1000);
        } else {
          const errorMsg =
            response.message || response.error?.message || response.error || 'Failed to add player';
          if (onError) onError(errorMsg);
        }
      } catch (err) {
        const errorMsg =
          err.response?.data?.message ||
          err.response?.data?.error?.message ||
          err.response?.data?.error ||
          err.message ||
          'Failed to add player';
        if (onError) onError(errorMsg);
      } finally {
        setAddingPlayer(null);
        setShowPositionModal(false);
        setPlayerForAssignment(null);
      }
    },
    [isCommissionerMode, activeTeamName, getTargetTeamId, leagueId, league, leagueStatus, loadRosterCapacity, navigate],
  );

  const handleBatchAddPlayers = useCallback(
    async (selectedPlayers, setError) => {
      if (!transactionsEnabled) {
        setError('Transactions are not allowed until prices are set');
        return;
      }
      if (selectedPlayers.length === 0) {
        setError('No players selected');
        return;
      }
      if (!rosterCapacityAnalysis) {
        await loadRosterCapacity();
        setError('Checking roster capacity...');
        setTimeout(() => setError(''), 3000);
        return;
      }
      const capacityCheck = canAddPlayers(selectedPlayers.length, rosterCapacityAnalysis);
      if (!capacityCheck.canAdd) {
        setError(`Cannot add ${selectedPlayers.length} players: ${capacityCheck.message}`);
        setTimeout(() => setError(''), 8000);
        return;
      }
      setShowBulkAssignmentModal(true);
    },
    [transactionsEnabled, rosterCapacityAnalysis, loadRosterCapacity],
  );

  const handleBulkAssignmentComplete = useCallback(
    async (assignmentData, callbacks = {}) => {
      const { clearAllSelections, onSuccess, onError } = callbacks;
      const results = { successful: [], failed: [] };

      try {
        for (const assignment of assignmentData) {
          try {
            const leaguePlayerId = assignment.player.ids?.league_player;
            const playerData = {
              league_player_id: leaguePlayerId,
              salary: assignment.salary,
              contract_years: assignment.contract_years,
              roster_status: assignment.roster_status,
              roster_position: assignment.roster_position,
              start_contract: assignment.start_contract,
            };
            if (isCommissionerMode) {
              playerData.commissioner_action = true;
              playerData.target_team_id = getTargetTeamId(null);
            }

            const response = await leaguesAPI.addPlayerToTeam(leagueId, playerData);
        invalidateLeaguePlayers(leagueId);
            if (response.success) {
              const firstName = assignment.player.info?.first_name;
              const lastName = assignment.player.info?.last_name;
              results.successful.push({
                player: assignment.player,
                name: response.player_name || `${firstName} ${lastName}`,
                position:
                  assignment.roster_status === 'active'
                    ? assignment.roster_position
                    : assignment.roster_status,
              });
            } else {
              const firstName = assignment.player.info?.first_name;
              const lastName = assignment.player.info?.last_name;
              results.failed.push({
                player: assignment.player,
                name: `${firstName} ${lastName}`,
                error: response.message || 'Unknown error',
              });
            }
          } catch (playerErr) {
            const firstName = assignment.player.info?.first_name;
            const lastName = assignment.player.info?.last_name;
            results.failed.push({
              player: assignment.player,
              name: `${firstName} ${lastName}`,
              error: playerErr.message || 'Network error',
            });
          }
        }

        if (results.successful.length > 0) {
          const teamName = isCommissionerMode ? activeTeamName : 'your team';
          if (onSuccess) {
            onSuccess(`Successfully added ${results.successful.length} players to ${teamName}!`);
          }
          if (clearAllSelections) clearAllSelections();

          await loadRosterCapacity();
          window.dispatchEvent(new Event('roster-updated'));

          navigate(`/leagues/${leagueId}`, { state: { activeSection: 'my-roster' } });
        }

        if (results.failed.length > 0) {
          const failedNames = results.failed.map((f) => f.name).join(', ');
          if (onError) onError(`Failed to add: ${failedNames}`);
        }
      } catch (err) {
        console.error('Error in bulk assignment completion:', err);
        if (onError) onError('Bulk assignment failed');
      } finally {
        setShowBulkAssignmentModal(false);
      }
    },
    [isCommissionerMode, activeTeamName, getTargetTeamId, leagueId, loadRosterCapacity, navigate],
  );

  // Initialize roster capacity on mount
  useEffect(() => {
    if (leagueId && league) {
      loadRosterCapacity();
    }
  }, [leagueId, league, loadRosterCapacity]);

  // Listen for roster-updated events (e.g., MyRoster move/DL/drop)
  useEffect(() => {
    const handleRosterUpdated = () => {
      if (leagueId && league) {
        loadRosterCapacity();
      }
    };
    window.addEventListener('roster-updated', handleRosterUpdated);
    return () => window.removeEventListener('roster-updated', handleRosterUpdated);
  }, [leagueId, league, loadRosterCapacity]);

  return {
    // Modal State
    showPositionModal,
    setShowPositionModal,
    showBulkAssignmentModal,
    setShowBulkAssignmentModal,
    playerForAssignment,
    setPlayerForAssignment,

    // Roster State
    currentRoster,
    rosterCapacityAnalysis,

    // UI State
    addingPlayer,
    setAddingPlayer,

    // Functions
    loadRosterCapacity,
    handleAddPlayer,
    executePlayerAssignment,
    handleBatchAddPlayers,
    handleBulkAssignmentComplete,
  };
};

export default useMobilePlayerAssignment;
